// Business page card: Client Portal (Premium plan feature). Without the plan
// it shows an upgrade prompt and no controls.
import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import clientPortalAPI from './clientPortalApi';
import ClientBalancePanel from './ClientBalancePanel';
import ClientBillingPanel from './ClientBillingPanel';
import StripeConnectCard from './StripeConnectCard';
import CatalogSettings from '../catalog/CatalogSettings';
import ClientPayoutAccounts from './ClientPayoutAccounts';
import PortalLinkSetting from './PortalLinkSetting';
import ClientCashouts from './ClientCashouts';
import { formatApiError } from '../../utils/errorMessage';

export function UpgradePrompt({ plans }) {
  const names = (plans || []).map((p) => p.name).filter(Boolean);
  return (
    <div className="border rounded p-3 text-sm bg-gray-50" data-testid="client-portal-upgrade">
      <p className="font-medium">Client Portal is part of the {names.length ? names.join(' or ') : 'Premium'} plan</p>
      <p className="text-gray-600">Give your clients their own sign-in to see invoices and payments.</p>
      <Link to="/billing" className="text-blue-600 underline">Upgrade this location</Link>
    </div>
  );
}

export default function ClientPortalSettings({ businessId }) {
  const [ent, setEnt] = useState(null);
  const [state, setState] = useState(null);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [link, setLink] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [openBalance, setOpenBalance] = useState(null);
  const [openBilling, setOpenBilling] = useState(null);

  const load = useCallback(async () => {
    try {
      const e = (await clientPortalAPI.entitlement(businessId)).data;
      setEnt(e);
      if (e.enabled) setState((await clientPortalAPI.get(businessId)).data);
    } catch (e) { setError(formatApiError(e, 'Could not load the Client Portal')); }
  }, [businessId]);
  useEffect(() => { if (businessId) load(); }, [businessId, load]);

  const run = async (fn, fallback) => {
    setBusy(true); setError('');
    try { await fn(); await load(); } catch (e) { setError(formatApiError(e, fallback)); }
    setBusy(false);
  };

  if (!businessId || !ent) return error ? <p role="alert" className="text-red-600 text-xs">{error}</p> : null;
  return (
    <section className="mb-4" aria-label="Client Portal">
      <h4 className="font-semibold text-sm mb-2">Client Portal</h4>
      {!ent.enabled ? <UpgradePrompt plans={ent.upgrade_plans} /> : state && (
        <div className="text-sm space-y-2">
          <label className="flex items-center gap-2">
            <input type="checkbox" checked={state.enabled} disabled={busy} aria-label="Turn on Client Portal"
              onChange={(e) => run(() => clientPortalAPI.setEnabled(businessId, e.target.checked), 'Could not save the setting')} />
            Let clients sign in to see their invoices and payments
          </label>
          {state.enabled && (
            <>
              <label className="flex items-center gap-2">
                <input type="checkbox" checked={state.auto_generate === true} disabled={busy} aria-label="Create recurring invoices automatically"
                  onChange={(e) => run(() => clientPortalAPI.saveSettings(businessId, { auto_generate: e.target.checked }), 'Could not save the setting')} />
                Create recurring invoices automatically each morning
              </label>
              <label className="flex items-center gap-2">
                <input type="checkbox" checked={state.notifications_enabled === true} disabled={busy} aria-label="Email clients about invoices"
                  onChange={(e) => run(() => clientPortalAPI.saveSettings(businessId, { notifications_enabled: e.target.checked }), 'Could not save the setting')} />
                Email clients when an invoice is sent, due soon, overdue, or paid
              </label>
              <label className="flex items-center gap-2">
                <input type="checkbox" checked={state.autopay_enabled === true} disabled={busy} aria-label="Offer autopay"
                  onChange={(e) => run(() => clientPortalAPI.saveSettings(businessId, { autopay_enabled: e.target.checked }), 'Could not save the setting')} />
                Let clients save a card for automatic payment of recurring invoices (needs Stripe connected)
              </label>
              <label className="flex items-center gap-2">
                <input type="checkbox" checked={state.payouts_enabled === true} disabled={busy} aria-label="Offer client payouts"
                  onChange={(e) => run(() => clientPortalAPI.saveSettings(businessId, { payouts_enabled: e.target.checked }), 'Could not save the setting')} />
                Let clients set up a payout account to receive cashouts from their client balance
              </label>
            </>
          )}
          {state.enabled && (
            <form className="flex flex-wrap gap-2" onSubmit={(e) => { e.preventDefault(); run(async () => { const r = await clientPortalAPI.invite(businessId, { email, display_name: name }); setLink(r.data.invite_url); setEmail(''); setName(''); }, 'Could not invite this client'); }}>
              <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Client email" aria-label="Client email" className="border rounded px-2 py-1" />
              <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Client name (optional)" aria-label="Client name" className="border rounded px-2 py-1" />
              <button type="submit" disabled={busy} className="border rounded px-3 py-1">Invite client</button>
            </form>
          )}
          {state.enabled && <PortalLinkSetting businessId={businessId} />}
          {state.enabled && <StripeConnectCard businessId={businessId} />}
          {state.enabled && <CatalogSettings businessId={businessId} />}
          {state.enabled && state.payouts_enabled === true && <ClientPayoutAccounts businessId={businessId} />}
          {state.enabled && state.payouts_enabled === true && <ClientCashouts businessId={businessId} clients={state.clients} />}
          {link && <p className="text-xs">Share this one-time invite link with the client (it expires in 7 days): <code className="break-all">{link}</code></p>}
          {state.clients.length > 0 && (
            <ul className="divide-y border rounded">
              {state.clients.map((c) => (
                <li key={c.id} className="px-2 py-1">
                  <div className="flex justify-between gap-2">
                    <span>{c.display_name ? `${c.display_name} (${c.email})` : c.email} - {c.status === 'invited' ? 'Invited' : c.status === 'active' ? 'Active' : 'Turned off'}</span>
                    <span className="flex gap-3">
                      <button type="button" className="text-blue-600 text-xs" onClick={() => setOpenBilling(openBilling === c.id ? null : c.id)}>{openBilling === c.id ? 'Hide billing' : 'Billing'}</button>
                      <button type="button" className="text-blue-600 text-xs" onClick={() => setOpenBalance(openBalance === c.id ? null : c.id)}>{openBalance === c.id ? 'Hide balance' : 'Balance'}</button>
                      {c.status !== 'disabled' && <button type="button" disabled={busy} className="text-red-600 text-xs" onClick={() => run(() => clientPortalAPI.disable(businessId, c.id), 'Could not turn off access')}>Turn off access</button>}
                    </span>
                  </div>
                  {openBilling === c.id && <ClientBillingPanel businessId={businessId} client={c} />}
                  {openBalance === c.id && <ClientBalancePanel businessId={businessId} client={c} />}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
      {error && <p role="alert" className="text-red-600 text-xs">{error}</p>}
    </section>
  );
}
